import { ageFromBirthDate } from 'tio-common/utils/date/age-from-birth-date';
import { Button } from 'tio-ui/components/buttons';
import { fn } from '@ember/helper';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Table from 'tio-ui/components/table';
import type DependentModel from 'tio-common/models/dependent';
import type EmployeeModel from 'tio-common/models/employee';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { Input } from 'tio-ui/components/forms';
import ReSelect from 'tio-reimagined/components/select';
import { PhPencil } from 'ember-phosphor-icons';

export interface DependentTableSignature {
  Args: {
    employee: EmployeeModel;
    renderInPlace?: boolean;
  };
}

const relationships = [
  { key: 'DependentRelationship.CHILD', value: 'Child' },
  { key: 'DependentRelationship.SPOUSE', value: 'Spouse' },
  { key: 'DependentRelationship.PARTNER', value: 'Partner' },
];

const getDependentRelationshipName = (dependent: DependentModel) => {
  return (
    relationships.find((relationship) => relationship.key === dependent.relationship)?.value ??
    'Unknown'
  );
};

export default class TuitionAssistanceDependentTable extends Component<DependentTableSignature> {
  @service declare store: StoreService;

  @tracked showEditDependentDialog = false;
  @tracked relationshipToEdit = '';
  @tracked dependentToEdit?: DependentModel;
  @tracked isConfirming = false;

  setRelationshipToEdit = (value: string | null) => {
    this.relationshipToEdit = value ?? '';
  };

  toggleEditDependentDialog = (dependent: DependentModel) => {
    this.dependentToEdit = dependent;
    this.relationshipToEdit =
      relationships.find((relationship) => dependent.relationship === relationship.key)?.key || '';
    this.showEditDependentDialog = !this.showEditDependentDialog;
  };

  submit = async () => {
    this.isConfirming = true;
    // @ts-expect-error fix type
    let dependentToUpdate = await this.store.findRecord<DependentModel>(
      'dependent',
      this.dependentToEdit?.id
    );

    dependentToUpdate.firstName = this.dependentToEdit!.firstName;
    dependentToUpdate.lastName = this.dependentToEdit!.lastName;
    // @ts-expect-error seems like a bug
    dependentToUpdate.age = this.dependentToEdit!.age;
    dependentToUpdate.birthDate = this.dependentToEdit!.birthDate;
    dependentToUpdate.relationship = this.relationshipToEdit;

    await dependentToUpdate.save();
    this.isConfirming = false;
    this.showEditDependentDialog = false;
  };

  cancel = async () => {
    await this.dependentToEdit!.rollbackAttributes();
    this.showEditDependentDialog = !this.showEditDependentDialog;
  };

  deleteDependentToEdit = async () => {
    // @ts-expect-error fix type
    let dependent = this.store.peekRecord<DependentModel>('dependent', this.dependentToEdit?.id);
    // @ts-expect-error not sure
    await dependent.destroyRecord();
    this.showEditDependentDialog = false;
  };

  get taDependents() {
    return this.args.employee.dependents.filter((d) => d.flavor === 'ta_dependent');
  }

  <template>
    <Table @isLoading={{false}}>
      <:thead as |options|>
        <options.tr>
          <options.th>{{t "common.tuition_assistance.dependent_table.header_name"}}</options.th>
          <options.th>
            {{t "common.tuition_assistance.dependent_table.header_relationship"}}
          </options.th>
          <options.th>{{t "common.tuition_assistance.dependent_table.header_age"}}</options.th>
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.taDependents as |dependent|}}
          {{#if dependent.notDeleted}}
            <options.tr>
              <options.td data-test-dependent-table-full-name>{{dependent.fullName}}</options.td>
              <options.td data-test-dependent-table-relationship>
                {{getDependentRelationshipName dependent}}
              </options.td>
              <options.td class="tracking-wide" data-test-dependent-table-age>
                <span class="flex w-full justify-between mr-4">
                  {{ageFromBirthDate dependent.birthDate}}
                  <button
                    type="submit"
                    class="text-tio-gray-400 hover:text-tio-gray-600 cursor-pointer"
                    {{on "click" (fn this.toggleEditDependentDialog dependent)}}
                    data-test-edit-dependent-button
                  >
                    <PhPencil @size="16" />
                  </button>
                </span>
              </options.td>
            </options.tr>
          {{/if}}
        {{/each}}
      </:tbody>
    </Table>
    <Modal
      @isOpen={{this.showEditDependentDialog}}
      @onClose={{this.cancel}}
      class="min-h-64"
      @size="xl"
      data-test-edit-dependent-dialog
    >
      <:header>{{t "common.tuition_assistance.edit_dependent_modal.header"}}</:header>
      <:body>
        <form class="md:col-span-2 pb-8">
          <div class="flex flex-col max-w-2xl">
            <div class="flex mb-6 mt-2 gap-x-4">
              <Input
                @label={{t "common.tuition_assistance.add_dependent_modal.first_name"}}
                @value={{this.dependentToEdit.firstName}}
                @onChange={{fn (mut this.dependentToEdit.firstName)}}
                data-test-edit-dependent-first-name
              />
              <Input
                @label={{t "common.tuition_assistance.add_dependent_modal.last_name"}}
                @value={{this.dependentToEdit.lastName}}
                @onChange={{fn (mut this.dependentToEdit.lastName)}}
                data-test-edit-dependent-last-name
              />
            </div>
            <div class="flex flex-col w-1/2">
              <div class="mt-2 mb-6">
                <ReSelect
                  @label={{t "common.tuition_assistance.add_dependent_modal.relationship"}}
                  @items={{relationships}}
                  @selected={{this.relationshipToEdit}}
                  @onChange={{this.setRelationshipToEdit}}
                  data-test-id="trigger"
                />
              </div>
              <div class="mt-2">
                <Input
                  @label={{t "common.tuition_assistance.add_dependent_modal.birth_date"}}
                  @type="date"
                  @value={{this.dependentToEdit.birthDate}}
                  @onChange={{fn (mut this.dependentToEdit.birthDate)}}
                  data-test-edit-dependent-birth-date
                />
              </div>
            </div>
          </div>
        </form>
      </:body>

      <:footer>
        <Footer
          @isRunning={{this.isConfirming}}
          @onSubmit={{this.submit}}
          @onCancel={{this.cancel}}
        >
          <:left>
            <Button @intent="danger" {{on "click" this.deleteDependentToEdit}}>
              {{t "common.tuition_assistance.edit_dependent_modal.delete_button"}}
            </Button>
          </:left>
        </Footer>
      </:footer>
    </Modal>
  </template>
}
